/**
 * Shared, pure `llms.txt` generator — ADR-0206 Slice 0 moved into
 * `@sill/types` so the edge handler (apps/edge/src/llms/handler.ts)
 * and the dashboard's Settings → AI Discovery preview
 * (apps/dashboard/src/views/settings/SettingsLlmsTxt.tsx) share
 * byte-for-byte identical output.
 *
 * The generator is PURE: no I/O, no clocks, no random. Same input →
 * byte-identical output. The handler assembles the input from the
 * site's KV state; the dashboard assembles it from the Site, its
 * live/published skills, and the merchant's compliance settings.
 *
 * Content shape mirrors the llmstxt.org convention (Jeremy Howard /
 * fast.ai, 2024): `# title`, a blockquote description, `##` sections
 * with `[label](url)` links per line.
 *
 * `## Agent Capabilities (MCP)` is emitted ONLY when the site has at
 * least one enabled skill. For a WEBSITE (`is_store === false`) the
 * generator omits the two store-only Discovery lines (AI catalog, UCP
 * business profile) and uses "site" rather than "store" wording in
 * the agent-page line. The default description, when the merchant has
 * not provided one, uses "Information and agent endpoints for
 * {domain}." for websites and "AI-ready commerce endpoint for
 * {domain}." for stores (the historical shape).
 *
 * `description` and `custom_sections` are the Slice-4 merchant-editable
 * fields. When undefined the generator falls back to the template
 * default and omits the custom section respectively. `custom_sections`
 * is appended verbatim (plain-text response, no HTML rendering).
 */
/**
 * Base URL for the per-site MCP endpoint. Drift-mirrored against
 * `MCP_ENDPOINT_TEMPLATE` in `apps/edge/src/card/handler.ts` and the
 * `/v1/mcp/:site_key` route registered in `apps/edge/src/index.ts`.
 */
export const LLMS_MCP_URL_BASE = 'https://edge.sill.so/v1/mcp/';
/**
 * Base URL for the per-site signed agent card. Drift-mirrored against
 * the card handler + the `/v1/agent-card/:site_key` route.
 */
export const LLMS_AGENT_CARD_URL_BASE = 'https://edge.sill.so/v1/agent-card/';
/**
 * Base URL for the per-site signed ARD catalog. Drift-mirrored against
 * the catalog handler + the `/v1/catalog/:site_key` route.
 */
export const LLMS_CATALOG_URL_BASE = 'https://edge.sill.so/v1/catalog/';
/** Human- and agent-readable HTML summary page (ADR-0208 /agent gateway). */
export const LLMS_AGENT_PAGE_URL_BASE = 'https://edge.sill.so/v1/agent/';
/** UCP business profile (ADR-0215), served for every published store site. */
export const LLMS_UCP_PROFILE_URL_BASE = 'https://edge.sill.so/v1/ucp/';
/** PAP discovery document (ADR-0252): `{base}{site_key_hash}/poppy.json`. */
export const LLMS_PAP_URL_BASE = 'https://edge.sill.so/v1/poppy/';
/**
 * Path prefix of the Sill Shopify App Proxy on a store's own domain
 * (`https://{store}/apps/sill/...`). Shopify reserves `/.well-known/`
 * and serves its own `/llms.txt`, `/agents.md` and `/.well-known/ucp`
 * on every store, so a Shopify page links Sill's documents here.
 */
export const SHOPIFY_APP_PROXY_PREFIX = '/apps/sill';
/** One line of merchant text, safe inside a Markdown list item. */
function oneLine(v, max = 200) {
    const t = v.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
    return t.length > max ? `${t.slice(0, max - 1).trim()}…` : t;
}
/**
 * Build the llms.txt Markdown body for one site. PURE FUNCTION:
 * deterministic over its input, no I/O, no clocks.
 *
 * The output always ends with a single trailing newline. Sections are
 * separated by a single blank line. When `custom_sections` is provided
 * it is appended verbatim after a single blank line.
 */
export function generateLlmsTxt(input) {
    const is_store = input.is_store !== false; // default true for back-compat
    const title = input.display_name.length > 0 ? input.display_name : input.domain;
    const default_description = is_store
        ? `AI-ready commerce endpoint for ${input.domain}.`
        : `Information and agent endpoints for ${input.domain}.`;
    const description = input.description !== undefined && input.description.length > 0
        ? input.description
        : default_description;
    const parts = [];
    parts.push(`# ${title}`);
    parts.push('');
    parts.push(`> ${description}`);
    // Website — the merchant's own site, when we know the real domain.
    if (input.real_domain === true) {
        parts.push('');
        parts.push('## Website');
        parts.push(`- [Home](https://${input.domain}/): ${oneLine(title)}`);
    }
    // Store policies — merchant-confirmed facts (the same values the signed
    // agent card carries) and policy page links. Omitted when empty.
    const f = input.facts ?? {};
    const policy_lines = [];
    if (f.return_window_days !== undefined && /^\d{1,4}$/.test(f.return_window_days)) {
        policy_lines.push(`- Returns: accepted within ${f.return_window_days} days`);
    }
    if (f.shipping_zones !== undefined && f.shipping_zones.trim().length > 0)
        policy_lines.push(`- Ships to: ${oneLine(f.shipping_zones)}`);
    if (f.currency !== undefined && f.currency.trim().length > 0)
        policy_lines.push(`- Currency: ${oneLine(f.currency, 8)}`);
    if (f.price_range !== undefined && f.price_range.trim().length > 0)
        policy_lines.push(`- Price range: ${oneLine(f.price_range)}`);
    if (f.subscription_billing !== undefined && f.subscription_billing.trim().length > 0) {
        policy_lines.push(`- Subscriptions: ${oneLine(f.subscription_billing, 64)}`);
    }
    if (input.policies?.terms_url !== undefined)
        policy_lines.push(`- [Terms of service](${input.policies.terms_url})`);
    if (input.policies?.privacy_url !== undefined)
        policy_lines.push(`- [Privacy policy](${input.policies.privacy_url})`);
    if (policy_lines.length > 0) {
        parts.push('');
        parts.push('## Store policies');
        parts.push(...policy_lines);
    }
    // Agent Capabilities — omit the whole section when there are no
    // enabled skills.
    if (input.capabilities.length > 0) {
        parts.push('');
        parts.push('## Agent Capabilities (MCP)');
        const mcp_url = `${LLMS_MCP_URL_BASE}${input.site_key_hash}`;
        for (const cap of input.capabilities) {
            parts.push(`- [${cap.name}](${mcp_url}): ${cap.description}`);
        }
    }
    // Discovery Endpoints — always include agent card, MCP server, agent
    // page. For stores we additionally advertise the AI catalog and UCP
    // business profile. For websites (SaaS marketing sites, blogs, docs
    // etc.) those two endpoints are not useful and are omitted.
    // Founder 2026-10-28 — white-label: with the merchant's real domain,
    // every discovery document is linked on that domain (the Worker and
    // plugin serve them there); the MCP server stays on Sill's edge.
    const on_domain = input.real_domain === true && input.domain.length > 0
        ? `https://${input.domain.toLowerCase()}`
        : null;
    // Shopify App Proxy layout: Sill's documents live under /apps/sill on
    // the store domain; PAP and the UCP profile are not proxied, so they
    // link Sill's copies (never Shopify's own /.well-known/ucp).
    const via_proxy = input.link_base === 'shopify_app_proxy' && on_domain !== null;
    const proxy = via_proxy ? `${on_domain}${SHOPIFY_APP_PROXY_PREFIX}` : null;
    parts.push('');
    parts.push('## Discovery Endpoints');
    parts.push(`- [Agent card](${proxy !== null ? `${proxy}/agent-card.json` : on_domain !== null ? `${on_domain}/.well-known/agent-card.json` : `${LLMS_AGENT_CARD_URL_BASE}${input.site_key_hash}.json`}): `
        + 'Signed agent identity and capabilities');
    if (is_store) {
        parts.push(`- [AI catalog](${proxy !== null ? `${proxy}/ai-catalog.json` : on_domain !== null ? `${on_domain}/ai-catalog.json` : `${LLMS_CATALOG_URL_BASE}${input.site_key_hash}.json`}): `
            + 'Structured product catalog');
    }
    parts.push(`- [MCP server](${LLMS_MCP_URL_BASE}${input.site_key_hash}): `
        + 'Model Context Protocol endpoint; tools/list shows what agents can do');
    parts.push(`- [Agent page](${proxy !== null ? `${proxy}/agent` : on_domain !== null ? `${on_domain}/agent` : `${LLMS_AGENT_PAGE_URL_BASE}${input.site_key_hash}`}): `
        + (is_store
            ? 'Plain summary of this store for assistants that read HTML'
            : 'Plain summary of this site for assistants that read HTML'));
    if (input.pap_enabled === true) {
        parts.push(`- [Personal agent sign-in (PAP)](${on_domain !== null && !via_proxy ? `${on_domain}/.well-known/poppy.json` : `${LLMS_PAP_URL_BASE}${input.site_key_hash}/poppy.json`}): `
            + 'Personal Agent Protocol discovery; agents sign in here before calling the MCP server');
    }
    if (is_store) {
        parts.push(`- [UCP business profile](${on_domain !== null && !via_proxy ? `${on_domain}/.well-known/ucp` : `${LLMS_UCP_PROFILE_URL_BASE}${input.site_key_hash}/profile`}): `
            + 'Universal Commerce Protocol profile');
    }
    // Custom sections — appended verbatim.
    if (input.custom_sections !== undefined &&
        input.custom_sections.trim().length > 0) {
        parts.push('');
        parts.push(input.custom_sections);
    }
    return `${parts.join('\n')}\n`;
}
//# sourceMappingURL=llms-txt.js.map